function getLocale() {
  const lang =
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("lang")
      : null;
  if (!lang) return "sv-SE";
  return lang.replace("_", "-");
}

function formatDateForDisplay(value) {
  if (!value) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return String(value);
  const [, y, m, d] = match.map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  try {
    return new Intl.DateTimeFormat(getLocale(), {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return String(value);
  }
}

class DapPanelController extends HTMLElement {
  #expanded = null;
  #compact = null;
  #summary = null;
  #onComplete = null;
  #onClick = null;
  #onSessionChange = null;
  #unsub = null;

  connectedCallback() {
    this.#expanded = this.querySelector("[data-dap-panel-expanded]");
    this.#compact = this.querySelector("[data-dap-panel-compact]");
    this.#summary = this.querySelector("[data-dap-compact-summary]");

    this.#onComplete = (e) => this.#collapse(e.detail ?? {});
    document.addEventListener("dap:session-complete", this.#onComplete);

    this.#onClick = (e) => this.#handleClick(e);
    this.addEventListener("click", this.#onClick);

    this.#onSessionChange = () => this.#syncFromSession();
    window.addEventListener("dap:session-change", this.#onSessionChange);

    this.#syncFromSession();
    this.#subscribeDap();
    requestAnimationFrame(() => {
      if (this.isConnected) this.#syncFromSession();
    });
  }

  disconnectedCallback() {
    if (this.#onComplete) {
      document.removeEventListener("dap:session-complete", this.#onComplete);
    }
    if (this.#onClick) {
      this.removeEventListener("click", this.#onClick);
    }
    if (this.#onSessionChange) {
      window.removeEventListener("dap:session-change", this.#onSessionChange);
    }
    this.#unsub?.();
    this.#unsub = null;
    this.#onComplete = null;
    this.#onClick = null;
    this.#onSessionChange = null;
  }

  #subscribeDap() {
    if (this.#unsub) return;
    if (window.DAP?.onSessionChange) {
      this.#unsub = window.DAP.onSessionChange(() => this.#syncFromSession());
      return;
    }
    requestAnimationFrame(() => {
      if (this.isConnected) this.#subscribeDap();
    });
  }

  #syncFromSession() {
    const valid = window.DAP?.isSessionValid?.();
    const session = valid ? window.DAP?.getSession?.() : null;
    if (session?.date && session?.address) {
      this.#collapse(session);
    } else {
      this.#expand();
    }
  }

  #handleClick(e) {
    const editBtn = e.target.closest("[data-dap-edit]");
    if (editBtn) {
      e.preventDefault();
      this.#expand();
      this.closest("delivery-picker")?.dispatchEvent(
        new CustomEvent("dap:request-recheck", { bubbles: false }),
      );
      return;
    }
    const resetBtn = e.target.closest("[data-dap-reset]");
    if (resetBtn) {
      e.preventDefault();
      this.closest("delivery-picker")?.dispatchEvent(
        new CustomEvent("dap:request-reset", { bubbles: false }),
      );
      this.#syncFromSession();
      return;
    }
    const calendarBtn = e.target.closest("[data-dap-open-calendar]");
    if (calendarBtn) {
      this.querySelector("dap-calendar-popover")?.open(calendarBtn);
    }
  }

  #collapse(detail) {
    if (!this.#expanded || !this.#compact) return;
    if (this.#summary) {
      const tpl = this.dataset.summaryTemplate || "__ADDRESS__ — __DATE__";
      const address = detail.address ?? "";
      const date = formatDateForDisplay(detail.date);
      this.#summary.textContent = tpl
        .replace("__ADDRESS__", address)
        .replace("__DATE__", date);
    }
    this.#expanded.setAttribute("hidden", "");
    this.#compact.removeAttribute("hidden");
    this.querySelector("dap-calendar-popover")?.close();
  }

  #expand() {
    if (!this.#expanded || !this.#compact) return;
    this.#expanded.removeAttribute("hidden");
    this.#compact.setAttribute("hidden", "");
  }
}

if (typeof customElements !== "undefined") {
  if (!customElements.get("dap-panel-controller")) {
    customElements.define("dap-panel-controller", DapPanelController);
  }
}

export { DapPanelController };
